import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';
import {
  pdfColors, pdfFontSize, pdfRadius, pdfSpacing,
  pdfStatusTones, type PdfTone,
} from '../theme';

interface Props {
  label: string;
  tone?: PdfTone;
  /** Compact pill — smaller padding, 6.8pt label. Use inside dense tables. */
  dense?: boolean;
}

const styles = StyleSheet.create({
  pill: {
    paddingVertical: 2,
    paddingHorizontal: pdfSpacing.sm,
    borderRadius: pdfRadius.pill,
    borderWidth: 0.5,
    borderStyle: 'solid',
    alignSelf: 'flex-start',
  },
  dense: {
    paddingVertical: 1,
    paddingHorizontal: 5,
  },
  text: {
    fontSize: pdfFontSize.xs,
    fontWeight: 600,
  },
  textDense: {
    fontSize: pdfFontSize.xxs,
  },
});

/** Status pill — matches the backend STATUS_TONE badges (bg + ink + border).
 *  Every state has a thin border, which is what gives the pill its printed
 *  weight at small sizes. Sentence case, so long statuses ("Assessment
 *  scheduled") stay on one line inside table cells. */
const Pill: React.FC<Props> = ({ label, tone = 'neutral', dense }) => {
  const t = pdfStatusTones[tone];
  return (
    <View
      style={[
        styles.pill,
        dense ? styles.dense : null,
        { backgroundColor: t.bg, borderColor: t.border },
      ].filter(Boolean) as any}
    >
      <Text
        style={[
          styles.text,
          dense ? styles.textDense : null,
          { color: t.fg },
        ].filter(Boolean) as any}
      >
        {label}
      </Text>
    </View>
  );
};

/** Compute a status tone from an admission ApplicationStatus string. Lives
 *  with the Pill component so every admission doc gets the same mapping. */
export function applicationStatusTone(status: string): PdfTone {
  const map: Record<string, PdfTone> = {
    Enrolled: 'success',
    Confirmed: 'success',
    OfferAccepted: 'success',

    Rejected: 'danger',
    Ineligible: 'danger',
    Withdrawn: 'danger',

    OfferDeclined: 'warning',
    OfferLapsed: 'warning',
    NotAdmitted: 'warning',
    Waitlisted: 'warning',
    AdmFeePending: 'warning',
    AppFeePending: 'warning',
    DocsRequired: 'warning',
    Absent: 'warning',

    Offered: 'primary',
    ConfirmedConditional: 'primary',

    Submitted: 'info',
    UnderReview: 'info',
    AssessmentScheduled: 'info',
    Assessed: 'info',
  };
  return map[status] || 'neutral';
}

// Expose pdfColors for consumers that already import Pill (keeps the public
// surface slim).
export { pdfColors };
export default Pill;
